/**
 * DocumentsPage Component
 * TASK-361: Document Management & Compliance System - Major Refactor
 *
 * Cross-client document management. TASK-819 renamed the tabs around the only
 * question the old labels never answered — WHO MADE THIS DOCUMENT:
 *
 * 1. Drafts & deliverables - what your firm produces (`documents` + exports,
 *    document-export service). Was "Files".
 * 2. Client evidence       - what clients send you (`client_documents`,
 *    data-ingestion). Was "All Documents", which was never all documents: it
 *    excluded every workbench document and deliverable.
 * 3. Expiring              - the same `client_documents` rows, filtered to
 *    those with an expiry date. Was "Compliance Dashboard", which also
 *    collided with the separate /compliance page of the same name.
 * 4. Quarantine Queue      - arrivals not yet accepted (`quarantine_documents`).
 *    Deliberately NOT renamed: since TASK-802 wired a real malware scanner,
 *    "quarantine" names a control that exists.
 * 5. Upload Links          - the secure links that produced them.
 *
 * Ordered by the document's lifecycle. Before, Quarantine sat between two tabs
 * reading the SAME table, which is part of why 2 and 3 looked unrelated.
 *
 * The `?tab=` values are NOT renamed — they are a linkable surface (see
 * OnboardingChecklist's `/documents?tab=quarantine`) and the content behind
 * each one is unchanged, so old links still land in the right place.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileEdit,
  FolderArchive,
  CalendarClock,
  Link2,
  Inbox,
} from 'lucide-react';
import SecondaryNav from '../components/layout/SecondaryNav';
import {
  DocumentComplianceDashboard,
  QuarantineQueue,
  SecureLinksTable,
  GenerateUploadLinkModal,
} from '../components/documents';
import AllDocumentsView from '../components/documents/AllDocumentsView';
import WorkbenchFilesTab from '../components/documents/WorkbenchFilesTab';
import { secureUploadLinkService } from '../services/secureUploadLinkService';

type TabValue = 'files' | 'all' | 'compliance' | 'quarantine' | 'links';

const VALID_TABS: TabValue[] = ['files', 'all', 'compliance', 'quarantine', 'links'];

const DocumentsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabValue | null;
  // TASK-606: the firm's own drafts are the front door; TASK-819 renamed that tab but
  // kept its `files` value, so existing /documents?tab=files links still land here.
  const initialTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'files';
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);
  const [pendingQuarantineCount, setPendingQuarantineCount] = useState(0);
  const [showCreateLinkModal, setShowCreateLinkModal] = useState(false);

  // Fetch pending quarantine count for badge
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const data = await secureUploadLinkService.getPendingCount();
        setPendingQuarantineCount(data.pending_count);
      } catch (err) {
        console.error('Failed to fetch pending count:', err);
      }
    };
    fetchPendingCount();
  }, []);

  // Tab configuration. TASK-654: each tab says WHICH document domain it shows —
  // the audit found the same nouns used for different domains with counts that
  // appeared to contradict. TASK-819 moved that answer into the LABELS instead
  // of leaving the captions to carry it alone: "Files" and "All Documents" are
  // synonyms in plain English, and nothing in either name said which one held
  // work you authored and which held evidence a client sent you.
  const tabs: {
    value: TabValue;
    label: string;
    icon: React.ReactNode;
    badge?: number;
    caption: string;
  }[] = [
    {
      value: 'files',
      label: 'Drafts & deliverables',
      icon: <FileEdit size={16} />,
      caption:
        'Documents your firm produces — working drafts you author (Word, Excel, PowerPoint) and the deliverables generated from them. Grouped by client.',
    },
    {
      value: 'all',
      label: 'Client evidence',
      icon: <FolderArchive size={16} />,
      caption:
        'Documents clients send you — IDs, contracts, statements, attestations. These are the source documents the KYC record is extracted from.',
    },
    {
      value: 'compliance',
      label: 'Expiring',
      icon: <CalendarClock size={16} />,
      caption:
        'The same client evidence, filtered to documents carrying an expiry date — what expires soon and what already has.',
    },
    {
      value: 'quarantine',
      label: 'Quarantine Queue',
      icon: <Inbox size={16} />,
      badge: pendingQuarantineCount,
      caption:
        'Arrivals that have been scanned but not yet accepted — not part of any client record until you review them.',
    },
    {
      value: 'links',
      label: 'Upload Links',
      icon: <Link2 size={16} />,
      caption: 'Secure links you sent to clients so they can upload evidence.',
    },
  ];
  const activeCaption = tabs.find((t) => t.value === activeTab)?.caption;

  return (
    <div className="min-h-screen bg-gray-50">
      <SecondaryNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Document Management</h2>
          <p className="text-gray-600">
            The documents your firm produces, and the evidence your clients send you
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap
                  transition-colors border-b-2 -mb-px
                  ${
                    activeTab === tab.value
                      ? 'text-[#002B45] border-[#002B45]'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className={`
                    px-1.5 py-0.5 text-xs font-medium rounded-full
                    ${activeTab === tab.value
                      ? 'bg-[#002B45] text-white'
                      : 'bg-amber-100 text-amber-800'
                    }
                  `}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
          {activeCaption && (
            <p className="px-4 py-2 text-xs text-gray-500" data-testid="documents-tab-caption">
              {activeCaption}
            </p>
          )}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {/* Drafts & deliverables Tab — TASK-606 */}
          {activeTab === 'files' && <WorkbenchFilesTab />}

          {/* Expiring Tab (client evidence filtered by expiry date) */}
          {activeTab === 'compliance' && (
            <DocumentComplianceDashboard warningDays={30} />
          )}

          {/* Quarantine Queue Tab */}
          {activeTab === 'quarantine' && (
            <QuarantineQueue
              onDocumentAccepted={() => setPendingQuarantineCount((c) => Math.max(0, c - 1))}
              onDocumentRejected={() => setPendingQuarantineCount((c) => Math.max(0, c - 1))}
            />
          )}

          {/* Upload Links Tab */}
          {activeTab === 'links' && (
            <SecureLinksTable
              onCreateLink={() => setShowCreateLinkModal(true)}
            />
          )}

          {/* Client evidence Tab */}
          {activeTab === 'all' && (
            <AllDocumentsView />
          )}
        </div>
      </main>

      {/* Create Link Modal with Client Selection */}
      <GenerateUploadLinkModal
        isOpen={showCreateLinkModal}
        onClose={() => setShowCreateLinkModal(false)}
        onSuccess={() => {
          setShowCreateLinkModal(false);
          // Refresh the links table if on links tab
          if (activeTab === 'links') {
            // The SecureLinksTable will re-fetch automatically
          }
        }}
      />
    </div>
  );
};

export default DocumentsPage;
